/**
 * useCareResources - Hook for managing Care (CCM) resources from Firestore
 *
 * Provides real-time updates of Care resources for an entity,
 * with fallback to demo data when no Firestore resources exist.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  onSnapshot,
  addDoc,
  deleteDoc,
  updateDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { CCM_RESOURCES } from '../components/Care/demoResources';

/**
 * Hook for managing Care resources
 * @param {string} entityId - The entity ID to fetch resources for
 * @param {boolean} useDemoFallback - Whether to use demo data when no Firestore data exists
 * @returns {Object} Care resources state and actions
 */
// eslint-disable-next-line max-lines-per-function
export function useCareResources(entityId, useDemoFallback = true) {
  const [resources, setResources] = useState({
    capacity: [],
    care: [],
    capital: [],
    community: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUsingDemoData, setIsUsingDemoData] = useState(false);

  // Subscribe to Firestore updates
  useEffect(() => {
    if (!entityId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Reference to the care subcollection
    const careRef = collection(db, 'entities', entityId, 'care');
    const careQuery = query(careRef);

    const unsubscribe = onSnapshot(
      careQuery,
      (snapshot) => {
        const firestoreResources = {
          capacity: [],
          care: [],
          capital: [],
          community: [],
        };

        snapshot.forEach((doc) => {
          const data = { id: doc.id, ...doc.data() };
          const category = data.ccmCategory;
          if (firestoreResources[category]) {
            firestoreResources[category].push(data);
          }
        });

        // Sort each category: pinned first, then by addedAt
        Object.keys(firestoreResources).forEach((category) => {
          firestoreResources[category].sort((a, b) => {
            if (a.isPinned && !b.isPinned) return -1;
            if (!a.isPinned && b.isPinned) return 1;
            const aTime = a.addedAt?.toMillis?.() || a.addedAt?.seconds * 1000 || 0;
            const bTime = b.addedAt?.toMillis?.() || b.addedAt?.seconds * 1000 || 0;
            return bTime - aTime;
          });
        });

        // Check if we have any Firestore data
        const hasFirestoreData = Object.values(firestoreResources).some(
          (arr) => arr.length > 0
        );

        if (hasFirestoreData) {
          setResources(firestoreResources);
          setIsUsingDemoData(false);
        } else if (useDemoFallback) {
          // Fall back to demo data
          setResources(CCM_RESOURCES);
          setIsUsingDemoData(true);
        } else {
          setResources(firestoreResources);
          setIsUsingDemoData(false);
        }

        setLoading(false);
      },
      (err) => {
        console.error('Error fetching Care resources:', err);
        setError(err.message);

        // Fall back to demo data on error
        if (useDemoFallback) {
          setResources(CCM_RESOURCES);
          setIsUsingDemoData(true);
        }

        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [entityId, useDemoFallback]);

  // Add a resource to Care
  const addResource = useCallback(
    async (resource) => {
      if (!entityId) {
        throw new Error('No entity selected');
      }

      const careRef = collection(db, 'entities', entityId, 'care');

      const newResource = {
        resourceEntityId: resource.resourceEntityId,
        ccmCategory: resource.ccmCategory || resource.suggestedCategory,
        relevanceTag: resource.relevanceTag,
        relevanceDetail: resource.relevanceDetail || '',
        name: resource.name,
        title: resource.title || '',
        organization: resource.organization || '',
        avatar: resource.avatar || '',
        entityType: resource.entityType,
        isPinned: false,
        pinnedAt: null,
        addedBy: 'ui', // Will be replaced by actual user ID in production
        addedAt: serverTimestamp(),
        addedVia: 'direct',
      };

      const docRef = await addDoc(careRef, newResource);
      return docRef.id;
    },
    [entityId]
  );

  // Remove a resource from Care
  const removeResource = useCallback(
    async (resourceId) => {
      if (!entityId) {
        throw new Error('No entity selected');
      }

      const docRef = doc(db, 'entities', entityId, 'care', resourceId);
      await deleteDoc(docRef);
    },
    [entityId]
  );

  // Pin/unpin a resource
  const togglePin = useCallback(
    async (resourceId, isPinned) => {
      if (!entityId) {
        throw new Error('No entity selected');
      }

      const docRef = doc(db, 'entities', entityId, 'care', resourceId);
      await updateDoc(docRef, {
        isPinned,
        pinnedAt: isPinned ? serverTimestamp() : null,
      });
    },
    [entityId]
  );

  // Move a resource within its category (reorder)
  const moveResource = useCallback(
    async (sectionId, resourceId, direction) => {
      // For now, this only works with local state
      // In production, you'd want to add an order field to Firestore
      setResources((prev) => {
        const section = [...prev[sectionId]];
        const index = section.findIndex((item) => item.id === resourceId);
        if (index === -1) return prev;

        const newIndex = direction === 'left' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= section.length) return prev;

        [section[index], section[newIndex]] = [section[newIndex], section[index]];
        return { ...prev, [sectionId]: section };
      });
    },
    []
  );

  // Get all resource IDs for checking if a resource is already added
  const getAllResourceIds = useCallback(() => {
    return Object.values(resources)
      .flat()
      .map((r) => r.resourceEntityId || r.id);
  }, [resources]);

  return {
    resources,
    loading,
    error,
    isUsingDemoData,
    addResource,
    removeResource,
    togglePin,
    moveResource,
    getAllResourceIds,
  };
}

export default useCareResources;
